import { AppShell, UnstyledButton, Group, Text, Stack, Box, Button } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import { IconMessage, IconInfoCircle, IconArrowLeft, IconSettings, IconCreditCard } from '@tabler/icons-react';
import { useMedplumProfile } from '@medplum/react';

import type { JSX } from "react";

interface NavbarProps {
  opened: boolean;
  onClose: () => void;
  setSelectedThreadId?: (id: string | null) => void;
  activeThreadId?: string | null;
  isFullScreenChat?: boolean;
  setIsFullScreenChat?: (value: boolean) => void;
  reflectionThreads?: { id: string; name: string; date: Date }[];
}

export function Navbar({ 
  opened, 
  onClose,
  setSelectedThreadId, 
  activeThreadId, 
  isFullScreenChat,
  setIsFullScreenChat,
  reflectionThreads = []
}: NavbarProps): JSX.Element {
  const navigate = useNavigate();
  const profile = useMedplumProfile();
  
  // Check if we're on the reflection page
  const isReflectionPage = window.location.pathname.includes('/reflection');
  
  // Exit full screen chat mode
  const exitFullScreenChat = () => {
    if (setIsFullScreenChat && setSelectedThreadId) {
      setIsFullScreenChat(false);
      setSelectedThreadId(null);
      onClose(); // Close navbar after action
    }
  };
  
  return (
    <AppShell.Navbar p="md" hidden={!opened} style={{ width: 300 }}>
      <div>
        {isReflectionPage && isFullScreenChat ? (
          <UnstyledButton onClick={exitFullScreenChat} mb="md">
            <Group>
              <IconArrowLeft size={16} />
              <Text>Back to Recent Sessions</Text>
            </Group>
          </UnstyledButton>
        ) : (
          <Text size="lg" fw={500} mb="md">Menu</Text>
        )}
      </div>
      <div style={{ flex: 1 }}>
        {isReflectionPage ? (
          // Show reflection-specific content when on reflection page
          (<>
            {/* Recent sessions - only show if we have threads */}
            {reflectionThreads && reflectionThreads.length > 0 && (
              <Box mb="xl">
                <Text fw={500} size="sm" mb="sm">Recent Sessions</Text>
                <Stack gap="xs">
                  {reflectionThreads.map((thread) => (
                    <UnstyledButton 
                      key={thread.id}
                      onClick={() => {
                        if (setSelectedThreadId) {
                          setSelectedThreadId(thread.id);
                          if (setIsFullScreenChat) {
                            setIsFullScreenChat(true);
                          }
                          onClose(); // Close navbar after selecting a thread
                        }
                      }}
                      py="xs"
                      style={(theme) => ({
                        borderRadius: theme.radius.sm,
                        backgroundColor: activeThreadId === thread.id ? 
                          'var(--mantine-color-gray-1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'var(--mantine-color-gray-0)',
                        },
                        paddingLeft: 8,
                        paddingRight: 8,
                      })}
                    >
                      <Group>
                        <IconMessage size={16} />
                        <Box>
                          <Text size="sm" lineClamp={1}>{thread.name}</Text>
                          <Text size="xs" c="dimmed">
                            {thread.date.toLocaleDateString(undefined, { 
                              month: 'short', 
                              day: 'numeric',
                              year: thread.date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                            })}
                          </Text>
                        </Box>
                      </Group>
                    </UnstyledButton>
                  ))}
                </Stack>
              </Box>
            )}
            {/* Start new conversation */}
            <Button 
              variant="light"
              color="violet"
              fullWidth
              mb="xl"
              onClick={() => {
                navigate('/reflection');
                if (setSelectedThreadId) {
                  setSelectedThreadId(null);
                }
                if (setIsFullScreenChat) {
                  setIsFullScreenChat(false);
                }
                onClose(); // Close navbar after action
              }}
            >
              New Conversation
            </Button>
          </>)
        ) : (
          // General navigation when not on reflection page
          (<Stack gap="xs">
            <UnstyledButton component={Link} to="/reflection" py="xs">
              <Group>
                <IconMessage size={16} />
                <Text>Reflection Guide</Text>
              </Group>
            </UnstyledButton>
          </Stack>)
        )}
        
        {/* Common links for all pages */}
        <Stack gap="xs" mt="xl">
          <Text fw={500} size="sm" mb="xs">Account</Text>
          <UnstyledButton component={Link} to="/account/profile" py="xs">
            <Group>
              <IconSettings size={16} />
              <Text>Profile</Text>
            </Group>
          </UnstyledButton>
          <UnstyledButton component={Link} to="/settings/subscription" py="xs">
            <Group>
              <IconCreditCard size={16} />
              <Text>Subscription</Text>
            </Group>
          </UnstyledButton>
        </Stack>
        
        <Stack gap="xs" mt="xl">
          <Text fw={500} size="sm" mb="xs">Information</Text>
          <UnstyledButton component={Link} to="/ai-ethics" py="xs">
            <Group>
              <IconInfoCircle size={16} />
              <Text>About AI Assistant</Text>
            </Group>
          </UnstyledButton>
        </Stack>
      </div>
      <div>
        <Text size="xs" c="dimmed" mb="md" mt="xl">
          {profile?.meta?.lastUpdated && `Last login: ${new Date(profile.meta.lastUpdated).toLocaleDateString()}`}
        </Text>
      </div>
    </AppShell.Navbar>
  );
}